import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { InvoiceItemsTable } from './InvoiceItemsTable';
import { GenerateFromTimeDialog } from './GenerateFromTimeDialog';
import { useClients } from '../../../core/hooks/useClients';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { Invoice, InvoiceItem } from '../../../core/models/Invoice';
import { InvoiceStatus } from '../../../core/models/Invoice';
import { format, addDays } from 'date-fns';
import { Clock } from 'lucide-react';

interface InvoiceFormProps {
  invoice?: Partial<Invoice>;
  onSubmit: (data: Omit<Invoice, 'id' | 'number' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onCancel: () => void;
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  onSubmit,
  onCancel,
}) => {
  const { organizationId } = useAuth();
  const { clients } = useClients(organizationId);
  const [items, setItems] = useState<InvoiceItem[]>(invoice?.items || []);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      clientId: invoice?.clientId || '',
      contractId: invoice?.contractId || '',
      date: invoice?.date ? format(invoice.date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      dueDate: invoice?.dueDate
        ? format(invoice.dueDate, 'yyyy-MM-dd')
        : format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      status: invoice?.status || InvoiceStatus.DRAFT,
      notes: invoice?.notes || '',
      terms: invoice?.terms || '',
    },
  });


  const handleItemsChange = (newItems: InvoiceItem[]) => {
    setItems(newItems);
  };

  const handleGenerateFromTime = (generatedItems: InvoiceItem[]) => {
    setItems([...items, ...generatedItems]);
    setGenerateDialogOpen(false);
  };

  const onFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
      const tax = subtotal * 0.1; // 10% tax
      const total = subtotal + tax;

      await onSubmit({
        organizationId: organizationId || '',
        clientId: data.clientId,
        contractId: data.contractId || undefined,
        date: new Date(data.date),
        dueDate: new Date(data.dueDate),
        items,
        subtotal,
        tax,
        total,
        status: data.status,
        notes: data.notes || undefined,
        terms: data.terms || undefined,
      });
    } catch (error) {
      console.error('Error submitting invoice:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Project <span className="text-error">*</span>
          </label>
          <Select
            value={watch('clientId')}
            onValueChange={(value) => setValue('clientId', value)}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.clientId && (
            <p className="text-sm text-error">{errors.clientId.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="Invoice Date"
            type="date"
            {...register('date', { required: 'Date is required' })}
            error={errors.date?.message}
          />

          <Input
            label="Due Date"
            type="date"
            {...register('dueDate', { required: 'Due date is required' })}
            error={errors.dueDate?.message}
          />
        </div>
      </div>

      <div className="flex items-center justify-between border-t pt-4">
        <h3 className="font-semibold">Invoice Items</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setGenerateDialogOpen(true)}
        >
          <Clock className="mr-2 h-4 w-4" />
          Generate from Time Entries
        </Button>
      </div>

      <InvoiceItemsTable items={items} onChange={handleItemsChange} />

      <Textarea
        label="Notes"
        {...register('notes')}
        rows={3}
        placeholder="Additional notes for the project"
      />

      <Textarea
        label="Terms & Conditions"
        {...register('terms')}
        rows={3}
        placeholder="Payment terms and conditions"
      />

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {invoice ? 'Update Invoice' : 'Create Invoice'}
        </Button>
      </div>

      <GenerateFromTimeDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onGenerate={handleGenerateFromTime}
        contractId={watch('contractId')}
      />
    </form>
  );
};
